import { Dialog } from "@headlessui/react";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import { useSelector, useDispatch } from "react-redux";
import { updateUserEmailOptions } from "service/userservice/updateUserEmailOptions";
import LoadingOverlay from "react-loading-overlay";
import {
  EverlakeLoadingSpinner,
  EverlakeLoadingOverlayStyle,
} from "components/General/EverlakeLoading";
import EConsentDialog from "./EConsentDialog";
import { updateUserInfo } from "redux/slices/userSlice";
import { useTranslation } from "react-i18next";

export default function UserNotificationDialogOnLogin({
  showDialog,
  setShowDialog,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { register, handleSubmit, reset } = useForm();
  const { policies } = useSelector((state) => state.data);
  const { csrUser } = useSelector((s) => s.csrUser);

  const [view, setView] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkBox, setCheckBox] = useState(false);
  const [checkBoxError, setCheckBoxError] = useState(false);
  let [openEConsentDialog, setOpenEConsentDialog] = useState(false);

  LoadingOverlay.propTypes = undefined;
  useEffect(() => {
    const totalPolicies = policies.length;
    const caPolicies = policies.filter((e) => e.issueState === "CA").length;
    // If all policies are from CA then go to view 4.
    caPolicies === totalPolicies ? setView(4) : setView(1);
  }, []);

  const resetSelection = (val) => {
    if (val === "cancel") {
      setCheckBox(false);
    } else if (val === "accept") {
      setCheckBox(true);
      setCheckBoxError(false);
    }
  };
  const getpolicyFlag = (inputflag) => {
    if (inputflag === "" || inputflag === "NN") {
      return "mail";
    } else if (inputflag === "EC" || inputflag === "EU") {
      return "eDelivery";
    } else if (inputflag === "PC" || inputflag === "PU") {
      return "both";
    }
  };

  const updateEmailOption = (flag) => {
    if (flag === "mail") {
      return "NN";
    } else if (flag === "eDelivery") {
      return "EC";
    } else if (flag === "both") {
      return "PC";
    }
  };

  const notEligibleMessage = () => {
    if (policies.find((e) => e.issueState.toUpperCase() === "CA")) {
      return t("UserNotificationDialogOnLogin-error");
    }
  };

  async function submit(data) {
    // E-Delivery should not be mandatory, if all the policies have mail option.
    const consentRequired = Object.values(data).filter(
      (data) => data && data !== "mail"
    );
    if (consentRequired.length && !checkBox) {
      setCheckBoxError(true);
    } else if (!csrUser?.userId) {
      if (view === 1) {
        policies.forEach(async (policy) => {
          const flag = data[`${policy.contractID}`];
          const updatedEmailOption = updateEmailOption(flag);
          const policyOwnerParty = policy.parties?.find(
            (x) => x.person?.type?.toLowerCase() === "primary owner"
          );
          setLoading(true);
          await updateUserEmailOptions(
            user?.userId,
            policy.contractID,
            policy.oldPolicy,
            updatedEmailOption,
            policyOwnerParty.person?.emailOption,
            policyOwnerParty.person?.partyTypeCode +
              policyOwnerParty?.person?.personId
          ).catch((error) => {
            console.log(error);
          });
        });
        if (consentRequired.length) {
          setView(2);
        } else {
          setView(3);
        }
        setLoading(false);
      } else {
        closeNotification();
      }
    } else {
      alert(t("UserNotificationDialogOnLogin-no-permission"));
      closeNotification();
    }
  }

  async function submitForCaUsers() {
    closeNotification();
    policies.forEach(async (policy) => {
      const updatedEmailOption = "NN";
      const policyOwnerParty = policy.parties?.find(
        (x) => x.person?.type?.toLowerCase() === "primary owner"
      );
      await updateUserEmailOptions(
        user?.userId,
        policy.contractID,
        policy.oldPolicy,
        updatedEmailOption,
        policyOwnerParty.person?.emailOption,
        policyOwnerParty.person?.partyTypeCode +
          policyOwnerParty?.person?.personId
      ).catch((error) => {
        console.log(error);
      });
    });
  }

  const closeNotification = () => {
    let { notificationPreference: _, ...updatedUser } = user;
    updatedUser.notificationPreference = 1;
    dispatch(updateUserInfo(updatedUser));
    setShowDialog(false);
    setView(1);
    setCheckBox(false);
    setCheckBoxError(false);
    reset();
  };

  return (
    <Dialog
      open={showDialog}
      onClose={() => closeNotification()}
      className="ui-dialogs"
    >
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      <DialogBackdrop />
      {/* Full-screen container to center the panel */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel
          role="dialog"
          aria-modal="true"
          className={`ui-default-dialog ${
            view === 1 && policies.length > 5 ? "bottom-[30%]" : ""
          }`}
        >
          <Dialog.Title className="font-body text-xl font-bold text-left">
            {t("UserNotificationDialogOnLogin-title")}
          </Dialog.Title>
          <hr />
          <LoadingOverlay
            active={loading}
            spinner={<EverlakeLoadingSpinner />}
            styles={EverlakeLoadingOverlayStyle}
          >
            {view === 1 && (
              <>
                <Dialog.Description className="font-body">
                  {t("UserNotificationDialogOnLogin-description")}
                </Dialog.Description>
                <form onSubmit={handleSubmit(submit)}>
                  <div className="grid gap-x-2 content-center auto-rows-min">
                    <div>{/* empty cell for grid */}</div>
                    <div className="text-sm font-semibold px-4 text-center justify-center">
                      {t("UserNotificationDialogOnLogin-e-delivery")}
                    </div>
                    <div className="text-sm font-semibold px-4 text-center justify-center">
                      {t("UserNotificationDialogOnLogin-mail")}
                    </div>
                    <div className="text-sm font-semibold px-4 text-center justify-center">
                      {t("UserNotificationDialogOnLogin-both")}
                    </div>
                    {/* E-Delivery Confirmation */}
                    {policies.map((policy, idx) => {
                      if (policy.issueState !== "CA") {
                        const policyOwnerParty = policy.parties?.find(
                          (x) =>
                            x.person?.type?.toLowerCase() === "primary owner"
                        );
                        const policyEmailOption =
                          policyOwnerParty?.person?.emailOption;
                        const flag = getpolicyFlag(policyEmailOption);
                        const policyId = policy.contractID;
                        return (
                          <fieldset
                            className="contents"
                            key={`policy notification - ${idx}`}
                          >
                            <legend className="text-base font-semibold py-3">
                              {t("UserNotificationDialogOnLogin-policy-number")}
                              {policy.oldPolicy}
                            </legend>
                            <label className="ui-checkbox relative place-self-center p-1">
                              <input
                                id={`emailOptions-eDelivery-${policyId}`}
                                key={`emailOptions-eDelivery-${policyId}`}
                                type="radio"
                                value="eDelivery"
                                defaultChecked={flag === "eDelivery"}
                                {...register(`${policyId}`)}
                              />
                              <span className="sr-only">
                                {t(
                                  "UserNotificationDialogOnLogin-notify-by-e-delivery"
                                )}
                              </span>
                              <div className={"ui-checkmark mx-2"}>
                                <svg
                                  className="ui-icon w-2 h-2 text-white"
                                  focusable="false"
                                  aria-hidden="true"
                                  role="presentation"
                                >
                                  <use href="#ui-icon-checkmark"></use>
                                </svg>
                              </div>
                            </label>

                            <label className="ui-checkbox relative place-self-center p-1">
                              <input
                                id={`emailOptions-mail-${policyId}`}
                                key={`emailOptions-mail-${policyId}`}
                                type="radio"
                                value="mail"
                                defaultChecked={flag === "mail"}
                                {...register(`${policyId}`)}
                              />
                              <span className="sr-only">notify by mail</span>
                              <div className="ui-checkmark mx-2">
                                <svg
                                  className="ui-icon w-2 h-2 text-white"
                                  focusable="false"
                                  aria-hidden="true"
                                  role="presentation"
                                >
                                  <use href="#ui-icon-checkmark"></use>
                                </svg>
                              </div>
                            </label>
                            <label className="ui-checkbox relative place-self-center p-1">
                              <input
                                id={`emailOptions-both-${policyId}`}
                                key={`emailOptions-both-${policyId}`}
                                type="radio"
                                value="both"
                                defaultChecked={flag === "both"}
                                {...register(`${policyId}`)}
                              />
                              <span className="sr-only">notify by both</span>
                              <div className={"ui-checkmark mx-2"}>
                                <svg
                                  className="ui-icon w-2 h-2 text-white"
                                  focusable="false"
                                  aria-hidden="true"
                                  role="presentation"
                                >
                                  <use href="#ui-icon-checkmark"></use>
                                </svg>
                              </div>
                            </label>
                            <hr className="ui-no-margins col-span-4" />
                          </fieldset>
                        );
                      }
                    })}
                    <p className="ui-no-margins col-span-4">
                      {notEligibleMessage()}
                    </p>
                  </div>

                  <label className="ui-checkbox">
                    <input
                      type="checkbox"
                      value={"checkbox"}
                      checked={checkBox}
                      onChange={() =>
                        checkBox
                          ? setCheckBox(false)
                          : setOpenEConsentDialog(true)
                      }
                    />
                    <div className="ui-checkmark mx-2">
                      {checkBox && (
                        <svg
                          className="ui-icon w-2 h-2 text-white"
                          focusable="false"
                          aria-hidden="true"
                          role="presentation"
                        >
                          <use href="#ui-icon-checkmark"></use>
                        </svg>
                      )}
                    </div>
                    <span className="font-semibold">
                      {t("UserNotificationDialogOnLogin-view1-disclaimer")}
                    </span>
                  </label>
                  {checkBoxError && (
                    <p className="ml-6 text-ui-red">
                      {t("UserNotificationDialogOnLogin-check-box-error")}
                    </p>
                  )}
                  {/* <!-- Buttons --> */}
                  <div className="block text-right mt-6">
                    <button className="ui-btn-primary ui-btn-min-w mt-4">
                      {t("UserNotificationDialogOnLogin-save")}
                      <span className="sr-only">
                        {t("UserNotificationDialogOnLogin-save")} changes to
                        your notificaiton settings
                      </span>
                    </button>
                  </div>
                </form>
              </>
            )}
          </LoadingOverlay>
          {view === 2 && (
            <>
              <div className="p-4 mx-auto md:p-6">
                <div className="text-xl text-center font-semibold">
                  {t("UserNotificationDialogOnLogin-view2-disclaimer")}
                </div>
                <div className="text-center mt-4 md:mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => closeNotification()}
                  >
                    {t("UserNotificationDialogOnLogin-close")}
                  </button>
                </div>
              </div>
            </>
          )}
          {view === 3 && (
            <>
              <div className="p-4 mx-auto md:p-6">
                <div className="text-xl text-center font-semibold">
                  {t("UserNotificationDialogOnLogin-view3-disclaimer")}
                </div>
                <div className="text-center mt-4 md:mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => closeNotification()}
                  >
                    {t("UserNotificationDialogOnLogin-close")}
                  </button>
                </div>
              </div>
            </>
          )}
          {view === 4 && (
            <div className="p-4 mx-auto md:p-6">
              <div className="text-xl text-center font-semibold">
                {t("UserNotificationDialogOnLogin-view4-disclaimer")}
              </div>
              <div className="text-center mt-4 md:mt-6">
                <button
                  type="button"
                  className="ui-btn-secondary ui-btn-min-w mt-4"
                  onClick={() => submitForCaUsers()}
                >
                  {t("UserNotificationDialogOnLogin-close")}
                  <span className="sr-only">close notificaiton settings</span>
                </button>
              </div>
            </div>
          )}
        </Dialog.Panel>
      </div>
      <EConsentDialog
        showEConsentDialog={openEConsentDialog}
        setshowEConsentDialog={setOpenEConsentDialog}
        resetSelection={resetSelection}
      />
    </Dialog>
  );
}
